import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { APP } from 'src/app/config/app';
import { CustomToggleTone, ExpeditionStatus, FilterType, TripsViewMode } from 'src/app/models/enums';
import { ICustomToggleOptions, IFilterPanelValues } from 'src/app/models';
import { FILTER_PANEL_ADAPTERS } from 'src/app/shared/filter-panel/filterPanelAdapters';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from '../../../../images';
import { StorageService, UtilsService } from 'src/app/services';
import {
  TRIP_FILTER_KEYS,
  hasAnyFilterKey,
  deserializeTripsFilters,
  parseTripsFiltersFromStorage,
  DEFAULT_TRIP_STATUS,
  ITripsFilters,
} from 'src/app/filters';

@Component({
  selector: 'app-trip-search-bar',
  templateUrl: './trip-search-bar.component.html',
  styleUrl: './trip-search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripSearchBarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() placeholder = '';
  @Input() searching = false;
  @Input() searchValue = '';
  @Input() viewMode = TripsViewMode.TRIPS;
  @Input() collapsed = false;
  @Output() filter = new EventEmitter();
  @Output() searchEventEmitter = new EventEmitter<string>();
  @Output() viewModeChange = new EventEmitter<TripsViewMode>();

  public filterOpened = false;
  public filtersApplied = 0;
  public filterTypes = FilterType;
  public readonly TripsViewMode = TripsViewMode;
  readonly viewModeToggleOptions: ICustomToggleOptions<TripsViewMode> = [
    {
      value: TripsViewMode.TRIPS,
      image: images.sidebar.trips,
      descriptionKey: 'trips.name',
      tone: CustomToggleTone.SECONDARY,
    },
    {
      value: TripsViewMode.POSITIONS,
      image: images.vehicles.position,
      descriptionKey: 'trips.positions.name',
      tone: CustomToggleTone.SECONDARY,
    },
  ];
  readonly filterFields = APP.TRIPS_FILTER_FIELDS;

  public images = images;

  private queryParamsSub?: Subscription;

  constructor(
    public literalService: LiteralService,
    public storageService: StorageService,
    public utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.updateFilters();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() =>
      this.updateFilters(),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewMode'] && !changes['viewMode'].firstChange) {
      this.updateFilters();
    }
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  get isPositions(): boolean {
    return this.viewMode === TripsViewMode.POSITIONS;
  }

  toggleViewMode() {
    this.viewModeChange.emit(this.isPositions ? TripsViewMode.TRIPS : TripsViewMode.POSITIONS);
  }

  search(event: any) {
    this.searchEventEmitter.emit(event);
  }

  onFilter(values: IFilterPanelValues) {
    this.filter.emit(FILTER_PANEL_ADAPTERS[FilterType.TRIPS]!.toFilters(values));
    this.updateFilters();
  }

  async updateFilters() {
    const filters = await this.resolveCurrentFilters();
    if (!filters) {
      this.filtersApplied = 0;
      return;
    }
    const filtersForCount: any = { ...filters };
    // The availability and itinerary filters are shown in the page title and have no control in the panel
    delete filtersForCount.selectedAvailabilityId;
    delete filtersForCount.selectedItineraryId;
    delete filtersForCount.selectedDrivers;
    delete filtersForCount.selectedVehicles;
    const isDefaultStatus = filtersForCount.selectedStatus?.length === DEFAULT_TRIP_STATUS.length
      && DEFAULT_TRIP_STATUS.every((s: ExpeditionStatus) => filtersForCount.selectedStatus.includes(s));
    if (isDefaultStatus || !filtersForCount.selectedStatus?.length) {
      filtersForCount.selectedStatus = [];
    }
    const dateRange = filtersForCount.from || filtersForCount.to;
    delete filtersForCount.from;
    delete filtersForCount.to;
    filtersForCount.dateRange = dateRange;
    this.filtersApplied = this.utilsService.getFiltersCount(filtersForCount);
  }

  private async resolveCurrentFilters(): Promise<ITripsFilters | null> {
    const params = this.activatedRoute.snapshot.queryParamMap;
    if (hasAnyFilterKey(params, TRIP_FILTER_KEYS)) {
      return deserializeTripsFilters(params);
    }
    return parseTripsFiltersFromStorage(
      await this.storageService.getTripsFilters(),
    );
  }
}
